import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/UI/Button';
import {
  AuthCard,
  AuthCardHeader,
  AuthField,
  AuthInputWrapper,
  authInputWithIconClass,
  AuthAlert,
  AuthLaunchTransition,
  AuthPageFrame,
  resolveLoginError,
  isEmailVerificationRequired,
} from '../../components/Auth';

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // Reset transient UI only when entering the page — not when AuthProvider re-renders
  // (clearError is unstable; depending on it cleared errors right after a failed login).
  useEffect(() => {
    clearError();
    setFormError(null);
    setLaunching(false);
    setPendingDestination(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayError = formError || error;

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setFormError(null);
      clearError();

      const destination = await login(data.email, data.password, searchParams.get('next') ?? undefined);
      setPendingDestination(destination);
      setLaunching(true);
    } catch (err: unknown) {
      if (isEmailVerificationRequired(err)) {
        const next = searchParams.get('next');
        const suffix = next ? `&next=${encodeURIComponent(next)}` : '';
        navigate(`/verify-email?email=${encodeURIComponent(data.email)}${suffix}`);
        return;
      }
      setFormError(resolveLoginError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AuthLaunchTransition
        active={launching}
        onComplete={() => {
          if (pendingDestination) navigate(pendingDestination);
        }}
      />
      <AuthPageFrame>
      <AuthCard>
        <AuthCardHeader
          title="Sign in"
          subtitle="Access your AI-powered finance workspace"
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <AuthField label="Email address" htmlFor="login-email" error={errors.email?.message}>
            <AuthInputWrapper hasError={!!errors.email || !!displayError}>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Mail className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
              </div>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                className={authInputWithIconClass(!!errors.email)}
                aria-invalid={!!errors.email || !!displayError}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
            </AuthInputWrapper>
          </AuthField>

          <AuthField label="Password" htmlFor="login-password" error={errors.password?.message}>
            <AuthInputWrapper hasError={!!errors.password || !!displayError}>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Lock className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
              </div>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className={`${authInputWithIconClass(!!errors.password)} pr-11`}
                aria-invalid={!!errors.password || !!displayError}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  validate: (value) => value.trim().length > 0 || 'Password cannot be empty',
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </AuthInputWrapper>
          </AuthField>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Forgot password?
            </Link>
          </div>

          {displayError ? (
            <AuthAlert title="Sign in failed">{displayError}</AuthAlert>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isLoading}
            className="w-full rounded-xl"
          >
            Sign in
          </Button>
        </form>
      </AuthCard>
      </AuthPageFrame>
    </>
  );
};

export default Login;
